import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Select,
  Alert,
  Input,
  Form,
  message,
  Tooltip,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import ModalWrapper from './ModalWrapper';
import { verifyFeatureAccess } from '../../global/featureFlags';
import { captureAnalytics } from '../../actions/preload';
import { AILabels } from '../../containers/walkthrough/constants';
import './settings-modal.scss';

const { Option } = Select;

const CATALOG_FEATURE = 'global_ai_category_catalog';

class AddCategory extends Component {
  constructor(props) {
    super(props);
    const category = props.modalType.data.category || {};

    this.state = {
      categoryEmrName: category.emr_category,
      AIInput: category.ai_tag,
      catalogSelection: category.ai_supported_document_category_id ?? null,
      editingCategoryID: category.emr_category ? category.id : '',
      canManageCategory: true,
      catalogEnabled: false,
      aiLabels: [],
    };
  }

  refreshCategories = async () => {
    const {
      getCategoriesByPage,
      getCategoriesByQuery,
      getCategoryCatalog,
      modalType,
    } = this.props;
    const { catalogEnabled } = this.state;
    const { refreshQuery } = modalType.data;

    const refreshCustom = refreshQuery ? getCategoriesByQuery(refreshQuery) : getCategoriesByPage();
    // After a link/unlink the catalog's per-org "linked elsewhere" set has changed,
    // so the upper panel needs a refetch — ManageCategories reads from the same slice.
    const refreshCatalog = (catalogEnabled && getCategoryCatalog) ? getCategoryCatalog() : null;

    await Promise.all([refreshCustom, refreshCatalog].filter(Boolean));
  };

  // eslint-disable-next-line react/no-arrow-function-lifecycle
  componentDidMount = async () => {
    const { modalType, catalog, getCategoryCatalog } = this.props;
    const category = modalType.data.category || {};
    const [canManageCategory, catalogEnabled] = await Promise.all([
      verifyFeatureAccess('manage_category'),
      verifyFeatureAccess(CATALOG_FEATURE),
    ]);

    // Catalog mode reuses the same store slice as the upper-panel UI. Refetch only
    // when the cache is empty — ManageCategories has likely populated it already.
    if (catalogEnabled && getCategoryCatalog && !(catalog?.items?.length)) {
      await getCategoryCatalog();
    }

    // Catalog mode owns the picker, so the legacy AI-tag list stays empty.
    const aiLabels = catalogEnabled ? [] : AILabels;

    this.setState({
      canManageCategory,
      catalogEnabled,
      aiLabels,
      editingCategoryID: category.emr_category ? category.id : '',
    });
  };

  buildPayload = () => {
    const {
      categoryEmrName, AIInput, catalogSelection, catalogEnabled,
    } = this.state;
    const base = { emr_category: categoryEmrName.trim() };

    if (catalogEnabled) {
      // Send null explicitly so the controller treats this as "unlink", not "leave alone".
      return { ...base, ai_supported_document_category_id: catalogSelection ?? null };
    }

    return { ...base, ai_tag: AIInput };
  };

  // Legacy orgs can pick any AI tag here, Biologics included, since the
  // biologics_detection flag was retired. Track which tag each saved category gets.
  trackLegacyAiTagSaved = (action) => {
    const { modalType } = this.props;
    const { catalogEnabled, AIInput } = this.state;
    if (catalogEnabled || !AIInput) return;
    const previousAiTag = modalType.data.category?.ai_tag || null;
    captureAnalytics('category_ai_tag_saved', {
      ai_tag: AIInput,
      action,
      ai_tag_changed: AIInput !== previousAiTag,
    });
  };

  addCategory = async () => {
    const { createNewCategory, hideModal } = this.props;
    const { categoryEmrName } = this.state;
    if (!categoryEmrName || categoryEmrName.trim() === '') return;

    const response = await createNewCategory(this.buildPayload());
    const errorCode = response?.response?.status;
    const errorMessageContent = (errorCode === 400) ? 'Error adding category. Duplicate emr category name already exists.' : 'Error adding category. Please try again.';
    hideModal();

    if (!response?.error) {
      this.trackLegacyAiTagSaved('created');
      message.success({
        content: 'Category successfully added',
      });
    } else {
      message.error({
        content: errorMessageContent,
      });
    }

    await this.refreshCategories();
  };

  editCategory = async () => {
    const { updateCategory, hideModal } = this.props;
    const { editingCategoryID } = this.state;

    const response = await updateCategory(editingCategoryID, this.buildPayload());
    const errorCode = response?.response?.status;
    const errorMessageContent = (errorCode === 400) ? 'Error updating category. Category and AI Tag must be unique' : 'Error updating category. Please try again.';
    hideModal();

    if (!response?.error) {
      this.trackLegacyAiTagSaved('updated');
      message.success({
        content: 'Category successfully updated',
      });
    } else {
      message.error({
        content: errorMessageContent,
      });
    }

    await this.refreshCategories();
  };

  catalogPickerOptions = () => {
    const { catalog } = this.props;
    const { editingCategoryID } = this.state;
    const items = catalog?.items || [];
    const editingId = editingCategoryID === '' ? null : Number(editingCategoryID);

    return items.filter((entry) => {
      // Hide rows already linked to *another* document_category in this org —
      // the model uniqueness validation would reject the save otherwise. Keep
      // the row that matches our editing target so the currently-linked value
      // stays visible while editing.
      const linkedDc = entry.organization_state?.document_category_id;
      if (linkedDc == null) return true;
      if (editingId && linkedDc === editingId) return true;
      return false;
    });
  };

  renderCatalogPicker = () => {
    const { modalType } = this.props;
    const { catalogSelection } = this.state;
    const { isAdmin } = modalType.data;
    const { canManageCategory } = this.state;
    const disableButton = canManageCategory && !isAdmin;
    const options = this.catalogPickerOptions();

    return (
      <>
        <Tooltip placement="left" title="Linking this EMR category to an AI catalog category lets Medsender's AI auto-classify incoming documents into it. Leave unlinked for manual-only use.">
          Link to AI catalog category (optional)
          <InfoCircleOutlined style={{ paddingLeft: '2px', marginTop: '6px', height: '8px' }} />
        </Tooltip>
        <div className="dropdown-container">
          <Select
            aria-label="Link to AI catalog category"
            style={{ width: 500, marginTop: 20 }}
            allowClear
            placeholder="None (manual-only)"
            onChange={value => this.setState({ catalogSelection: value ?? null })}
            value={catalogSelection || undefined}
            disabled={disableButton}
          >
            {options.map(entry => (
              <Option key={entry.id} value={entry.id}>
                {entry.display_name}
              </Option>
            ))}
          </Select>
        </div>
      </>
    );
  };

  renderLegacyAiTagPicker = () => {
    const { modalType } = this.props;
    const { isAdmin } = modalType.data;
    const { AIInput, aiLabels, canManageCategory } = this.state;
    const disableButton = canManageCategory && !isAdmin;

    return (
      <>
        <Tooltip placement="left" title="When a Cateogry is assigned to an AI tag, any time our AI classifies a document with this value we'll automatically apply this category.">
          Assign AI tag (optional)
          <InfoCircleOutlined style={{ paddingLeft: '2px', marginTop: '6px', height: '8px' }} />
        </Tooltip>
        <div className="dropdown-container">
          <Select
            aria-label="Assign AI tag"
            style={{ width: 500, marginTop: 20 }}
            onChange={event => this.setState({ AIInput: event })}
            value={AIInput}
            disabled={disableButton}
          >
            {aiLabels.map(aiVal => <Option key={aiVal} value={aiVal}>{aiVal}</Option>)}
          </Select>
        </div>
      </>
    );
  };

  renderModal = () => {
    const { isLoading, modalType } = this.props;
    const { isAdmin } = modalType.data;
    const {
      categoryEmrName,
      canManageCategory,
      catalogEnabled,
    } = this.state;
    const disableButton = canManageCategory && !isAdmin;
    if (isLoading) {
      return (<span> Adding new category... </span>);
    }
    return (
      <Form
        layout="vertical"
        onFinish={!this.state.editingCategoryID && this.addCategory}
        id="addCategory"
      >
        <Form.Item
          hasFeedback
          name="categoryEmrName"
          label={(
            <div>
              EMR Category Name
              <Tooltip title="This is the dropdown list value of the category you want to be selected in your EMR during Auto-Upload. The category will be displayed in Medsender under the Document Title.">
                <InfoCircleOutlined style={{ paddingLeft: '2px', marginTop: '6px', height: '8px' }} />
              </Tooltip>
            </div>
          )}
          rules={[
            {
              required: true,
              message: 'Must provide a emr category name',
            },
          ]}
        >
          <Input
            value={categoryEmrName}
            placeholder={categoryEmrName}
            onChange={event => this.setState({ categoryEmrName: event.target.value })}
            disabled={disableButton}
          />
        </Form.Item>
        {catalogEnabled ? this.renderCatalogPicker() : this.renderLegacyAiTagPicker()}
      </Form>
    );
  };

  render() {
    const { isLoading, hideModal, modalType: { data: { isAdmin } } } = this.props;
    const { editingCategoryID, canManageCategory, categoryEmrName } = this.state;
    const disableButton = canManageCategory && !isAdmin;
    const customContentStyle = {
      width: '340px',
    };

    return (
      <ModalWrapper
        wrapClassName="settings-modal-wrap"
        hideModal={hideModal}
        customContentStyle={customContentStyle}
        action={!editingCategoryID ? this.addCategory : this.editCategory}
        actionName={editingCategoryID ? 'Save' : 'Add'}
        dismiss={isLoading ? '' : 'Cancel'}
        disabled={disableButton || isLoading || (!categoryEmrName && !editingCategoryID)}
        form="addCategory"
        modalTitle={editingCategoryID ? 'Edit Category' : 'Add New Category'}
      >
        <div onKeyPress={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
        >
          {disableButton && (
            <div style={{ marginBottom: '25px' }}>
              <Alert
                message="You do not have permissions to add/edit/delete categories. Please contact your admin to make any changes."
                type="info"
                showIcon
              />
            </div>
          )}
          {this.renderModal()}
        </div>
      </ModalWrapper>
    );
  }
}

AddCategory.defaultProps = {
  error: false,
  errorCode: null,
  catalog: { items: [], isLoading: false, error: null },
  getCategoryCatalog: null,
};

AddCategory.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  getCategoriesByPage: PropTypes.func.isRequired,
  getCategoriesByQuery: PropTypes.func.isRequired,
  updateCategory: PropTypes.func.isRequired,
  createNewCategory: PropTypes.func.isRequired,
  catalog: PropTypes.shape({
    items: PropTypes.array,
    isLoading: PropTypes.bool,
    error: PropTypes.any,
  }),
  getCategoryCatalog: PropTypes.func,
  error: PropTypes.bool,
  errorCode: PropTypes.string,
};

export default connect((state) => ({
  isLoading: state.categories.isLoading,
  error: state.categories.error,
  errorCode: state.categories.errorCode,
}))(AddCategory);
